import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import Table from '../../components/UI/Table';
import Modal from '../../components/UI/Modal';
import { trainingAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  GraduationCap, 
  Calendar,
  MapPin,
  Users,
  Clock,
  Eye,
  CheckCircle,
  XCircle,
  Settings
} from 'lucide-react';

const TrainingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  margin: 0;
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
`;

const FilterCard = styled(Card)`
  padding: 1.5rem;
  margin-bottom: 1rem;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 1rem;
  align-items: end;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 200px;
`;

const FilterLabel = styled.label`
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.primary};
`;

const Select = styled.select`
  padding: 0.75rem 1rem;
  font-size: ${props => props.theme.typography.fontSize.base};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.white};
  color: ${props => props.theme.colors.text.primary};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary + '20'};
  }
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  
  ${props => {
    switch (props.status) {
      case 'upcoming':
        return `
          background-color: ${props.theme.colors.info + '20'};
          color: ${props.theme.colors.info};
        `;
      case 'ongoing':
        return `
          background-color: ${props.theme.colors.warning + '20'};
          color: ${props.theme.colors.warning};
        `;
      case 'completed':
        return `
          background-color: ${props.theme.colors.success + '20'};
          color: ${props.theme.colors.success};
        `;
      default:
        return `
          background-color: ${props.theme.colors.gray[100]};
          color: ${props.theme.colors.text.secondary};
        `;
    }
  }}
`;

const TypeBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  background-color: ${props => props.theme.colors.primary + '20'};
  color: ${props => props.theme.colors.primary};
`;

const TrainingPrograms = () => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    type: '',
    status: ''
  });
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  const { theme } = useTheme();
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();

  const fetchPrograms = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        per_page: 100,
        is_active: true,
      };
      if (filters.search) params.search = filters.search;
      if (filters.type) params.training_type = filters.type;
      if (filters.status) params.status = filters.status;

      const response = await trainingAPI.getAll(params);
      const payload = response.data?.data || response.data;
      setPrograms(Array.isArray(payload) ? payload : []);
    } catch (error) {
      console.error('Error fetching training programs:', error);
      toast.error('Unable to load training programs. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filters.search, filters.type, filters.status]);

  useEffect(() => {
    fetchPrograms();
  }, [fetchPrograms]);
  
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };
  
  const openProgramModal = async (program) => {
    setLoadingDetails(true);
    try {
      const response = await trainingAPI.getById(program.id);
      setSelectedProgram(response.data);
      setShowModal(true);
    } catch (error) {
      console.error('Error loading training details:', error);
      toast.error('Unable to load training details.');
    } finally {
      setLoadingDetails(false);
    }
  };
  
  const handleDeleteProgram = async (programId) => {
    if (window.confirm('Are you sure you want to delete this training program?')) {
      try {
        await trainingAPI.delete(programId);
        toast.success('Training program deleted.');
        setShowModal(false);
        fetchPrograms();
      } catch (error) {
        console.error('Error deleting training program:', error);
        toast.error('Failed to delete training program.');
      }
    }
  };
  
  const getProgramStatus = (startDate, endDate) => {
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (now < start) return 'upcoming';
    if (now >= start && now <= end) return 'ongoing';
    return 'completed';
  };
  
  const getTypeDisplayName = (type) => {
    const typeNames = {
      workshop: 'Workshop',
      seminar: 'Seminar',
      conference: 'Conference',
      online_course: 'Online Course',
      field_training: 'Field Training'
    };
    return typeNames[type] || type;
  };
  
  const columns = [
    {
      header: 'Title',
      accessor: 'title',
      width: '300px'
    },
    {
      header: 'Type',
      accessor: 'training_type',
      render: (value) => <TypeBadge theme={theme}>{getTypeDisplayName(value)}</TypeBadge>
    },
    {
      header: 'Duration',
      accessor: 'duration_hours',
      render: (value) => `${value} hours`
    },
    {
      header: 'Start Date',
      accessor: 'start_date',
      render: (value) => new Date(value).toLocaleDateString()
    },
    {
      header: 'Location',
      accessor: 'location'
    },
    {
      header: 'Status',
      accessor: 'start_date',
      render: (value, row) => {
        const status = getProgramStatus(value, row.end_date);
        return <StatusBadge theme={theme} status={status}>{status}</StatusBadge>;
      }
    },
    {
      header: 'Participants',
      accessor: 'max_participants',
      render: (value) => `${value || 0} max`
    },
    {
      header: 'Actions',
      accessor: 'id',
      render: (value, row) => (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button
            size="small"
            variant="ghost"
            onClick={() => openProgramModal(row)}
            leftIcon={<Eye size={14} />}
          >
            View
          </Button>
          {hasPermission('edit_training') && (
            <Button
              size="small"
              variant="ghost"
              onClick={() => openProgramModal(row)}
              leftIcon={<Edit size={14} />}
            >
              Edit
            </Button>
          )}
          {hasPermission('delete_training') && (
            <Button
              size="small"
              variant="ghost"
              onClick={() => handleDeleteProgram(row.id)}
              leftIcon={<Trash2 size={14} />}
            >
              Delete
            </Button>
          )}
        </div>
      )
    }
  ];
  
  return (
    <TrainingContainer theme={theme}>
      <Header theme={theme}>
        <Title theme={theme}>Training Programs</Title>
        <HeaderActions>
          {hasPermission('manage_training') && (
            <Button
              variant="outline"
              leftIcon={<Settings size={16} />}
              onClick={() => navigate('/app/training/manage')}
            >
              Manage Trainings
            </Button>
          )}
          {hasPermission('add_training') && (
            <Button 
              variant="primary" 
              leftIcon={<Plus size={16} />}
              onClick={() => navigate('/app/training/manage?mode=create')}
            >
              Add Training
            </Button>
          )}
        </HeaderActions>
      </Header>
      
      <FilterCard theme={theme}>
        <FilterRow theme={theme}>
          <FilterGroup theme={theme}>
            <FilterLabel theme={theme}>Search</FilterLabel>
            <Input
              placeholder="Search programs..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              leftIcon={<Search size={16} />}
            />
          </FilterGroup>
          
          <FilterGroup theme={theme}>
            <FilterLabel theme={theme}>Type</FilterLabel>
            <Select
              theme={theme}
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
            >
              <option value="">All Types</option>
              <option value="workshop">Workshop</option>
              <option value="seminar">Seminar</option>
              <option value="conference">Conference</option>
              <option value="online_course">Online Course</option>
              <option value="field_training">Field Training</option>
            </Select>
          </FilterGroup>
          
          <FilterGroup theme={theme}>
            <FilterLabel theme={theme}>Status</FilterLabel>
            <Select
              theme={theme}
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="upcoming">Upcoming</option>
              <option value="ongoing">Ongoing</option>
              <option value="completed">Completed</option>
            </Select>
          </FilterGroup>
        </FilterRow>
      </FilterCard>
      
      <Table
        columns={columns}
        data={programs}
        loading={loading}
        onRowClick={openProgramModal}
        emptyMessage="No training programs found"
      />
      
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={selectedProgram?.title || ''}
        size="large"
      >
        {selectedProgram && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {loadingDetails && <p>Loading details...</p>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <strong>Type:</strong> {getTypeDisplayName(selectedProgram.training_type)}
              </div>
              <div>
                <strong>Duration:</strong> {selectedProgram.duration_hours} hours
              </div>
              <div>
                <strong>Start Date:</strong> {new Date(selectedProgram.start_date).toLocaleDateString()}
              </div>
              <div>
                <strong>End Date:</strong> {new Date(selectedProgram.end_date).toLocaleDateString()}
              </div>
              <div>
                <strong>Location:</strong> {selectedProgram.location}
              </div>
              <div>
                <strong>Max Participants:</strong> {selectedProgram.max_participants}
              </div>
              <div>
                <strong>Trainer:</strong> {selectedProgram.trainer_name}
              </div>
              <div>
                <strong>Organization:</strong> {selectedProgram.trainer_organization}
              </div>
            </div>
            
            <div>
              <strong>Description:</strong>
              <p style={{ marginTop: '0.5rem', lineHeight: '1.5' }}>
                {selectedProgram.description}
              </p>
            </div>
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              {hasPermission('edit_training') && (
                <Button variant="primary" leftIcon={<Edit size={16} />}>
                  Edit Program
                </Button>
              )}
              <Button variant="outline" onClick={() => setShowModal(false)}>
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </TrainingContainer>
  );
};

export default TrainingPrograms;
